import { useQueryParams } from "hooks/useQueryParams";
import { useCallback, useEffect, useMemo, useRef, useState, startTransition } from "react";
import { useOmnisearchContext } from "../../providers/OmnisearchContext";

/**
 * STATE DIAGRAM - useOmnisearchInput
 * ==================================
 *
 * REFS (no re-renders):
 * +-------------------------------------------------------------------+
 * |  immediateValueRef       - Current typing value (every keystroke) |
 * |  pendingUpdateRef        - Debounce timer ID                      |
 * |  hasUserSetOmnisearchRef - Dirty flag (user has interacted)       |
 * |  isUpdatingURLRef        - Guard: we initiated URL change         |
 * |  skipNextURLSyncRef      - Guard: skip duplicate URL sync         |
 * |  isInitialRender         - Guard: first render                    |
 * +-------------------------------------------------------------------+
 *
 * STATE (triggers re-renders):
 * +-------------------------------------------------------------------+
 * |  debouncedValue          - Only value consumers see               |
 * +-------------------------------------------------------------------+
 *
 * DATA FLOW:
 *
 *   User Types                        Programmatic Change
 *       |                                    |
 *       v                                    v
 * +-------------+                   +------------------+
 * |setOmnisearch|                   |setOmnisearchImm- |
 * | (debounced) |                   |ediate (instant)  |
 * +------+------+                   +--------+---------+
 *        |                                   |
 *        v                                   |
 * +--------------+                           |
 * |Update ref    |                           |
 * |Start 500ms   |                           |
 * |timer         |                           |
 * +------+-------+                           |
 *        |                                   |
 *        | (timer fires)                     |
 *        v                                   v
 * +----------------------------------------------+
 * |           setDebouncedValue(val)             |
 * |         (triggers consumer re-render)        |
 * +----------------------+-----------------------+
 *                        |
 *            +-----------+-----------+
 *            v                       v
 *       [isURLDriven]          [isContextDriven]
 *            |                       |
 *            v                       v
 * +--------------+         +--------------+
 * | Sync to URL  |         |Sync to       |
 * |(setQueryPar- |         |Context       |
 * | ams)         |         |              |
 * +--------------+         +--------------+
 *
 * EXTERNAL SYNC (URL or Context changes from outside):
 *
 *   URL Change              Context Change
 *   (popstate,              (parent sets
 *    navigate)               context)
 *       |                        |
 *       +--------+---------------+
 *                v
 *       +----------------+
 *       |syncFromExternal|
 *       | - Update ref   |
 *       | - Clear timer  |
 *       | - Update state |
 *       +----------------+
 */

interface UseOmnisearchInputOptions {
  isURLDriven: boolean;
  initialFilters?: { field: string; value: string | boolean }[];
}

const DEBOUNCE_MS = 500; // Debounce delay for typing

/**
 * useOmnisearchInput manages the omnisearch input value.
 *
 * Architecture:
 * - This hook maintains internal state that updates on every keystroke
 * - It returns ONLY the debounced value as `omnisearch` to prevent consumer re-renders during typing
 * - The `setOmnisearch` callback is stable and updates internal state (debounced)
 * - The `setOmnisearchImmediate` callback updates immediately (for programmatic clears, filter applies)
 * - The Omnisearch component manages its own local TextField state for immediate display
 * - Consumer components (OmnisearchDataDisplay, etc.) only re-render after debounce
 *
 * If inside an OmnisearchProvider, it syncs with the context's state.
 * If `isURLDriven` is true, the debounced value is synced with the `omnisearch` query param.
 */
export function useOmnisearchInput({
  isURLDriven,
  initialFilters = [],
}: UseOmnisearchInputOptions) {
  const omnisearchContext = useOmnisearchContext();
  const isContextDriven = !!omnisearchContext;

  const contextOmnisearch = omnisearchContext?.omnisearch;
  const setContextOmnisearch = omnisearchContext?.setOmnisearch;
  const hasUserSetOmnisearchContext = omnisearchContext?.hasUserSetOmnisearch;

  const initialFilterOmnisearch = useMemo(() => {
    return initialFilters.length
      ? initialFilters.map((f) => `${f.field}:${f.value}`).join(" ")
      : "";
  }, [initialFilters]);

  const { filters: queryParams, setFilters: setQueryParams } = useQueryParams<{
    omnisearch?: string;
  }>();

  // Determine initial value precedence: URL > Context > Initial Filters > Empty
  const getInitialValue = useCallback(() => {
    if (isURLDriven && queryParams.omnisearch) {
      return queryParams.omnisearch;
    }
    if (isContextDriven && contextOmnisearch) {
      return contextOmnisearch;
    }
    return initialFilterOmnisearch;
  }, [
    isURLDriven,
    queryParams.omnisearch,
    isContextDriven,
    contextOmnisearch,
    initialFilterOmnisearch,
  ]);

  // Track if user has interacted with omnisearch (dirty flag)
  // Using ref because this doesn't need to trigger re-renders - it only guards logic
  const hasUserSetOmnisearchRef = useRef(false);
  
  // Internal state for the immediate value - updates on every keystroke
  // We use useRef to track the immediate value without causing re-renders
  // Only the debounced state triggers consumer re-renders
  const immediateValueRef = useRef<string>(getInitialValue());
  const [debouncedValue, setDebouncedValue] = useState<string>(getInitialValue);
  
  const isInitialRender = useRef(true);
  const pendingUpdateRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Track when we're updating the URL ourselves to avoid re-syncing our own changes
  const isUpdatingURLRef = useRef(false);
  // Skip the URL sync effect after setOmnisearchImmediate already synced to URL
  // This prevents duplicate navigate() calls which cause React Router delays
  const skipNextURLSyncRef = useRef(false);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pendingUpdateRef.current) {
        clearTimeout(pendingUpdateRef.current);
      }
    };
  }, []);

  // Update immediately - for programmatic changes (clear, filter apply, etc.)
  const setOmnisearchImmediate = useCallback((val: string) => {
    immediateValueRef.current = val;
    hasUserSetOmnisearchRef.current = true;

    // Clear any pending debounce
    if (pendingUpdateRef.current) {
      clearTimeout(pendingUpdateRef.current);
      pendingUpdateRef.current = null;
    }

    const valueToSync = val.trim();

    if (isURLDriven) {
      isUpdatingURLRef.current = true;
      skipNextURLSyncRef.current = true;

      // CRITICAL: Update URL SYNCHRONOUSLY using history.replaceState before React state update.
      // This ensures useSyncExternalStore reads the new URL during the same render cycle.
      // React Router's navigate() is async and causes timing issues where queries fire
      // with stale data before the URL is actually updated.
      const params = new URLSearchParams(window.location.search);
      if (valueToSync) {
        params.set("omnisearch", valueToSync);
      } else {
        params.delete("omnisearch");
      }
      const newSearch = params.toString();
      const newUrl = newSearch
        ? `${window.location.pathname}?${newSearch}`
        : window.location.pathname;

      // Synchronous URL update for useSyncExternalStore
      history.replaceState(null, "", newUrl);

      // Update React Router state (for useLocation() consumers and tests)
      // NOTE: setQueryParams uses navigate() which has its own startTransition internally,
      // so we must NOT wrap this in startTransition to avoid nested transition conflicts
      setQueryParams((prev) => {
        const newParams = { ...prev };
        if (!valueToSync) {
          delete newParams.omnisearch;
        } else {
          newParams.omnisearch = valueToSync;
        }
        return newParams;
      });
    }

    // Wrap our own state updates in startTransition so Suspense boundaries
    // show stale data instead of fallback
    startTransition(() => {
      // Only update context directly if NOT URL-driven
      if (isContextDriven && setContextOmnisearch && !isURLDriven) {
        setContextOmnisearch(valueToSync);
      }

      // Trigger React state update - useSyncExternalStore will see new URL
      setDebouncedValue(val);
    });
  }, [isContextDriven, setContextOmnisearch, isURLDriven, setQueryParams]);

  // setOmnisearch updates the ref immediately and schedules a debounced state update
  // This allows the Omnisearch component to call this on every keystroke
  // without triggering consumer re-renders until debounce completes
  const setOmnisearch = useCallback((val: string) => {
    immediateValueRef.current = val;
    hasUserSetOmnisearchRef.current = true;
    
    // Clear any pending update
    if (pendingUpdateRef.current) {
      clearTimeout(pendingUpdateRef.current);
    }
    
    // Schedule debounced state update
    pendingUpdateRef.current = setTimeout(() => {
      setDebouncedValue(val);
      pendingUpdateRef.current = null;
    }, DEBOUNCE_MS);
  }, []);

  // Sync from external sources (URL changes, context changes)
  // These should update both ref and state immediately
  const syncFromExternal = useCallback((newValue: string) => {
    immediateValueRef.current = newValue;
    if (pendingUpdateRef.current) {
      clearTimeout(pendingUpdateRef.current);
      pendingUpdateRef.current = null;
    }
    setDebouncedValue(newValue);
  }, []);

  // Effect: Update when URL omnisearch value changes
  useEffect(() => {
    const wasInitialRender = isInitialRender.current;
    isInitialRender.current = false;
    
    // If we're the source of this URL update, don't sync back (would overwrite user's typing)
    if (isUpdatingURLRef.current) {
      isUpdatingURLRef.current = false;
      return;
    }
    
    if (isURLDriven && queryParams.omnisearch !== immediateValueRef.current) {
      if (wasInitialRender && !queryParams.omnisearch) {
        return;
      }
      syncFromExternal(queryParams.omnisearch ?? "");
    }
  }, [queryParams.omnisearch, isURLDriven, syncFromExternal]);

  // Effect: Update when context omnisearch value changes
  useEffect(() => {
    if (!isURLDriven && isContextDriven && immediateValueRef.current !== contextOmnisearch) {
      syncFromExternal(contextOmnisearch ?? "");
    }
  }, [contextOmnisearch, isURLDriven, isContextDriven, syncFromExternal]);

  // Effect: Sync hasUserSetOmnisearch from context
  useEffect(() => {
    if (isContextDriven && hasUserSetOmnisearchContext && !hasUserSetOmnisearchRef.current) {
      hasUserSetOmnisearchRef.current = true;
    }
  }, [hasUserSetOmnisearchContext, isContextDriven]);

  // Effect: Sync debounced value to Context
  // Only for context-driven mode (non-URL-driven)
  // URL-driven mode: URL is single source of truth, OmnisearchProvider syncs from URL to context
  useEffect(() => {
    if (isURLDriven) return; // URL is single source of truth in URL-driven mode

    if (!hasUserSetOmnisearchRef.current && !initialFilterOmnisearch && !queryParams.omnisearch) {
      if (!isContextDriven) return;
    }

    const valueToSync = debouncedValue.trim();
    if (isContextDriven && setContextOmnisearch) {
      setContextOmnisearch(valueToSync);
    }
  }, [
    debouncedValue,
    isContextDriven,
    isURLDriven,
    setContextOmnisearch,
    initialFilterOmnisearch,
    queryParams.omnisearch,
  ]);

  // Effect: Sync debounced value to URL
  useEffect(() => {
    if (!isURLDriven) return;
    
    // Skip if setOmnisearchImmediate already synced to URL
    // This prevents duplicate navigate() calls which cause React Router delays
    if (skipNextURLSyncRef.current) {
      skipNextURLSyncRef.current = false;
      return;
    }

    const valueToSync = debouncedValue.trim();

    // Mark that we're updating the URL ourselves - the URL change effect should ignore this
    isUpdatingURLRef.current = true;
    
    setQueryParams((prev) => {
      const currentQueryParam = prev.omnisearch ?? "";
      if (valueToSync === currentQueryParam) {
        isUpdatingURLRef.current = false;
        return prev; // Explicitly return prev to signal no change
      }
      const newParams = { ...prev };
      if (!valueToSync) {
        delete newParams.omnisearch;
      } else {
        newParams.omnisearch = valueToSync;
      }
      return newParams;
    });
  }, [debouncedValue, isURLDriven, setQueryParams]);

  // Effect: Set initial query param if needed
  useEffect(() => {
    if (
      isURLDriven &&
      !hasUserSetOmnisearchRef.current &&
      !queryParams.omnisearch &&
      initialFilterOmnisearch
    ) {
      setQueryParams((prev) => {
        prev.omnisearch = initialFilterOmnisearch;
      });
    }
  }, [
    isURLDriven,
    queryParams.omnisearch,
    initialFilterOmnisearch,
    setQueryParams,
  ]);

  // Helper functions use the debounced value for consistency
  const getOmnisearchValue = useCallback(
    (key: string) => {
      const { kvpValues } = breakApartOmnisearch(debouncedValue);
      const kvp = kvpValues.find(([k]) => k === key);
      return kvp?.[1];
    },
    [debouncedValue]
  );

  const createUrlFromOmnisearchKeyValues = useCallback(
    (params: { keyValues: [string, string][]; keysToKeep?: string[] }) => {
      const { keyValues, keysToKeep } = params;
      let omnisearch = "";
      
      if (keysToKeep) {
        const { kvpValues } = breakApartOmnisearch(debouncedValue);
        for (const keyToKeep of keysToKeep) {
          const existingKvp = kvpValues.find(([k]) => k === keyToKeep);
          if (existingKvp) {
            omnisearch = addOrUpdateKeyInOmnisearch(omnisearch, existingKvp[0], existingKvp[1]);
          }
        }
      } else {
        omnisearch = debouncedValue;
      }
      
      for (const [key, value] of keyValues) {
        omnisearch = addOrUpdateKeyInOmnisearch(omnisearch, key, value);
      }
      
      return `${window.location.protocol}//${window.location.host}${
        location.pathname
      }?${
        omnisearch.length ? `omnisearch=${encodeURIComponent(omnisearch)}` : ""
      }`;
    },
    [debouncedValue]
  );

  const editOmnisearchKeyValues = useCallback(
    (keyVals: [string, string][]) => {
      let current = debouncedValue;
      for (const [key, value] of keyVals) {
        current = addOrUpdateKeyInOmnisearch(current, key, value);
      }
      // Use immediate for programmatic edits (filter popover, etc.)
      setOmnisearchImmediate(current);
    },
    [debouncedValue, setOmnisearchImmediate]
  );

  const removeOmnisearchKeyValue = useCallback(
    (key: string) => {
      // Use immediate for programmatic removals
      setOmnisearchImmediate(removeKeyInOmnisearch(debouncedValue, key));
    },
    [debouncedValue, setOmnisearchImmediate]
  );

  return {
    // Return debounced value - consumers only re-render after debounce
    // The Omnisearch component manages its own local state for immediate TextField display
    omnisearch: debouncedValue,
    debouncedOmnisearch: debouncedValue,
    getOmnisearchValue,
    createUrlFromOmnisearchKeyValues,
    setOmnisearch,              // Debounced - for typing
    setOmnisearchImmediate,     // Immediate - for clear, filter apply, etc.
    editOmnisearchKeyValues,
    removeOmnisearchKeyValue,
  };
}

function addOrUpdateKeyInOmnisearch(
  search: string,
  key: string,
  value: string
) {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(search);
  const filteredKvp = kvpValues.filter(([k]) => k !== key);
  const baseOmnisearch = `${nonKvpValues} ${filteredKvp
    .map(([k, v]) => `${k}:${v}`)
    .join(" ")}`.trim();
  const newKeyValue = `${key}:${value}`;
  return baseOmnisearch ? `${baseOmnisearch} ${newKeyValue}` : newKeyValue;
}

function removeKeyInOmnisearch(search: string, key: string) {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(search);
  const filteredKvp = kvpValues.filter(([k]) => k !== key);
  return `${nonKvpValues} ${filteredKvp
    .map(([k, v]) => `${k}:${v}`)
    .join(" ")}`.trim();
}

function breakApartOmnisearch(search: string) {
  const tokens = parseOmnisearchTokens(search);
  const nonKvpValues: string[] = [];
  const kvpValues: [string, string][] = [];
  
  for (const token of tokens) {
    const colonIndex = token.indexOf(":");
    if (colonIndex > 0) {
      const key = token.substring(0, colonIndex);
      const value = token.substring(colonIndex + 1);
      kvpValues.push([key, value]);
    } else {
      nonKvpValues.push(token);
    }
  }
  
  return { 
    nonKvpValues: nonKvpValues.join(" "), 
    kvpValues 
  };
}

function parseOmnisearchTokens(search: string): string[] {
  const tokens: string[] = [];
  let current = "";
  let inQuotes = false;
  let quoteChar = "";
  
  for (let i = 0; i < search.length; i++) {
    const char = search[i];
    
    if (!inQuotes && (char === '"' || char === "'")) {
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (inQuotes && char === quoteChar) {
      inQuotes = false;
      quoteChar = "";
      current += char;
    } else if (!inQuotes && char === " ") {
      if (current.trim()) {
        tokens.push(current.trim());
        current = "";
      }
    } else {
      current += char;
    }
  }
  
  if (current.trim()) {
    tokens.push(current.trim());
  }
  
  return tokens;
}
